// @ts-check
// v4 UUIDs for the checkout money path.
//
// The order endpoint creates the order AND starts the payment, and its tail runs
// past our 30s budget on a large bundle. A key minted here identifies ONE checkout
// ATTEMPT and travels with every retry of that attempt as the `Idempotency-Key`
// header, so the backend returns the order the first call made instead of making a
// second one. A key that changed between retries would be worse than no key at all
// — it would look like a genuinely new order and buy the hardware twice — so this
// value must be minted deliberately, once, and held (see AddressInput's ref).
//
// `crypto.randomUUID` needs a SECURE CONTEXT: it is undefined on plain http, which
// includes local dev over http://<lan-ip>. `getRandomValues` has no such
// restriction, so it carries the common fallback; `Math.random` is the last resort
// that keeps checkout working on anything ancient rather than throwing on the way
// to Stripe. Uniqueness is all this value needs — it is never a secret and never
// guessed by anyone.

/**
 * A fresh v4 UUID.
 * @returns {string}
 */
export function newIdempotencyKey() {
  const webCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined

  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }

  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10xx
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}
